import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { cx } from '@/lib/cx';

/** Largeur du panneau, en pixels. */
const LARGEUR = 300;
/** Marge minimale entre le panneau et le bord de la fenêtre. */
const MARGE = 8;

/**
 * Définition au clic d'un indicateur : un « i » discret à côté du libellé, qui
 * ouvre un court texte et, au besoin, un lien vers la page Méthode.
 *
 * Le panneau est en position fixe, calculée à l'ouverture : dans un tableau
 * défilant (`overflow-x-auto`), une position absolue serait rognée. Il se
 * referme au clic extérieur, à la touche Échap et au défilement. Tout est en
 * `span` : l'infobulle se pose dans un libellé `<p>` ou un en-tête `<th>`, où
 * un `div` serait invalide.
 */
export function Infobulle({
  libelle,
  children,
  lien,
  className,
}: {
  /** Nom accessible du bouton, par exemple « Définition de l'indice ». */
  libelle: string;
  children: ReactNode;
  /** Section de la page Méthode, pour aller plus loin (« /methode#indice »). */
  lien?: string;
  className?: string;
}) {
  const [position, setPosition] = useState<{ haut: number; gauche: number } | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const panneau = useRef<HTMLSpanElement>(null);
  const id = useId();
  const ouvert = position !== null;

  const ouvrir = () => {
    const r = bouton.current?.getBoundingClientRect();
    if (!r) return;
    const gauche = Math.min(
      Math.max(MARGE, r.left + r.width / 2 - LARGEUR / 2),
      window.innerWidth - LARGEUR - MARGE,
    );
    setPosition({ haut: r.bottom + 6, gauche: Math.max(MARGE, gauche) });
  };

  useEffect(() => {
    if (!ouvert) return;
    const fermer = () => setPosition(null);
    const surClic = (e: MouseEvent) => {
      const cible = e.target as Node;
      if (!bouton.current?.contains(cible) && !panneau.current?.contains(cible)) fermer();
    };
    const surTouche = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        fermer();
        bouton.current?.focus();
      }
    };
    document.addEventListener('mousedown', surClic);
    document.addEventListener('keydown', surTouche);
    // `true` : le défilement d'un conteneur intérieur compte aussi.
    window.addEventListener('scroll', fermer, true);
    window.addEventListener('resize', fermer);
    return () => {
      document.removeEventListener('mousedown', surClic);
      document.removeEventListener('keydown', surTouche);
      window.removeEventListener('scroll', fermer, true);
      window.removeEventListener('resize', fermer);
    };
  }, [ouvert]);

  return (
    <span className="relative inline-flex align-middle">
      <button
        ref={bouton}
        type="button"
        aria-label={libelle}
        aria-expanded={ouvert}
        aria-controls={ouvert ? id : undefined}
        onClick={(e) => {
          // Dans une ligne cliquable, l'ouverture ne doit pas déclencher la ligne.
          e.stopPropagation();
          if (ouvert) setPosition(null);
          else ouvrir();
        }}
        className={cx(
          'inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-ink-subtle transition-colors hover:text-ink',
          ouvert && 'text-ink',
          className,
        )}
      >
        <Info aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
      {position && (
        <span
          ref={panneau}
          id={id}
          role="note"
          style={{ top: position.haut, left: position.gauche, width: LARGEUR }}
          className="fixed z-50 block rounded-card border border-line bg-surface p-3 text-left text-caption font-normal normal-case leading-relaxed tracking-normal text-ink shadow-float"
        >
          <span className="block">{children}</span>
          {lien && (
            <Link
              to={lien}
              onClick={() => setPosition(null)}
              className="mt-2 inline-block font-medium text-primary hover:underline"
            >
              En savoir plus sur la méthode
            </Link>
          )}
        </span>
      )}
    </span>
  );
}
